import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { PromptsRemote } from "@modules/messaging-prompts/domain/infrastructure/prompts.remote";
import { PromptsModel } from "@modules/messaging-prompts/domain/entities/prompts.model";
import { NotificationsRoutingFacade } from "@modules/notifications/domain/+state/notifications-routing-facade.service";

export interface PromptBannerHome {
	showBanner: boolean;
	promptsData: PromptsModel[];
}

@Injectable({
	providedIn: "root"
})
export class PromptsFacade {
	private readonly routingFacade = inject(NotificationsRoutingFacade);
	private readonly promptsRemote = inject(PromptsRemote);

	public promptsData$$ = new BehaviorSubject<PromptsModel[]>([]);
	public readonly promptsData$: Observable<PromptBannerHome> = this.promptsData$$.asObservable().pipe(
		map((prompts) => {
			return {
				showBanner: prompts.length > 0,
				promptsData: prompts
			};
		})
	);

	public apcPromptEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(true);

	public disableApcPromptStatus(): void {
		this.apcPromptEnabled$.next(false);
	}

	public getPromptsData(): Observable<PromptsModel[]> {
		return this.promptsRemote.getPrompts$;
	}

	public fetchPromptsData(): void {
		this.promptsRemote.getPrompts$.pipe(take(1)).subscribe({
			next: (prompts) => {
				this.promptsData$$.next(prompts);
			},
			error: () => {
				this.promptsData$$.next([]);
			}
		});
	}

	private fetchPostPromptData(pid: string): void {
		this.promptsData$$.next(this.promptsData$$.value.filter((prompt) => prompt.id !== pid));
		this.promptsRemote.postPrompt$(pid).pipe(take(1)).subscribe();
	}

	public updatePromptsData(pid: string, caseId: string): void {
		if (pid) {
			this.fetchPostPromptData(pid);
			return;
		}
		if (caseId) {
			this.promptsData$$.getValue().some((prompt) => {
				const caseID = new URLSearchParams(prompt.event.targetPath.split("?")[1]).get("case_id");
				if (caseID === caseId) {
					this.fetchPostPromptData(prompt.id);
				}
			});
		}
	}

	public onTargetPathClicked(target: string, eventPid: string): void {
		this.routingFacade.getTarget(target, eventPid);
	}
}
